import { T } from '@tolgee/react';
import { Modal } from 'antd';
import Link from 'next/link';
import { FunctionComponent } from 'react';
import styled from 'styled-components';

import { CuriConfused } from '../../../assets/icons/CuriConfused';
import { CuripodAIStarsSvg } from '../../../assets/icons/CuripodAIOrangeFilledSvg';
import { LogoSvg } from '../../../assets/icons/LogoSvg';
import theme from '../../../assets/theme';
import { useWindowSize } from '../../../hooks/useWindowSize';
import Metrics from '../../../services/metrics';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';

interface UsagePaywallProps {
  visible: boolean;
  onClose: () => void;
  message?: React.ReactNode;
}

const CuripodAi = styled.span`
  color: #ffa154;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const DueToHigh = styled.b`
  max-width: 650px;
  width: 100%;
  position: relative;
  font-size: 18px;
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const YoureUnableTo = styled.b`
  max-width: 518px;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const InfoBox = styled.div`
  width: 100%;
  box-shadow: 0px 2.5999999046325684px 5.6px rgba(0, 0, 0, 0.13),
    0px 0.8999999761581421px 1.3px rgba(0, 0, 0, 0.11);
  border-radius: 10px;
  background-color: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  box-sizing: border-box;
  gap: 10px;
  max-width: 700px;
  font-size: 16px;
  color: #111;

  @media (max-width: 768px) {
    padding: 15px;
    gap: 15px;
    flex-direction: column;
    text-align: center;
  }

  @media (max-width: 480px) {
    padding: 10px;
    gap: 10px;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 30px;

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const ToKeepMaking = styled.b`
  position: relative;
  display: inline-block;
  max-width: 700px;

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 20px;
  text-align: center;
  font-size: 18px;

  @media (max-width: 768px) {
    font-size: 16px;
    gap: 15px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;

  @media (max-width: 480px) {
    width: 100%;
    gap: 10px;

    & > button {
      width: 100%;
      max-width: 250px;
    }
  }
`;

const ChatLink = styled.span`
  font-size: 12px;
  color: ${theme.colors.black};
  cursor: pointer;
  text-decoration: underline;
  margin-top: 5px;
  transition: all 0.2s ease;

  &:hover {
    color: ${theme.colors.orange};
    text-decoration: none;
  }
`;

const MainContainer = styled.div`
  width: 100%;
  position: relative;
  background-color: #f9f3eb;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 30px 76px 35px;
  box-sizing: border-box;
  gap: 30px;
  max-width: 1000px;
  text-align: left;

  @media (max-width: 768px) {
    padding: 20px 30px 25px;
    gap: 15px;
  }

  @media (max-width: 480px) {
    padding: 15px 20px 20px;
    gap: 10px;
  }
`;

const Title = styled.h1`
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    font-size: 24px;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

export const UsagePaywall: FunctionComponent<UsagePaywallProps> = ({
  visible,
  onClose,
}) => {
  const [windowWidth] = useWindowSize();
  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={windowWidth < 600 ? '95%' : 1000}
      closable={true}
      maskClosable={true}
      className="trial-capacity-paywall"
      style={{ top: 10 }}
      bodyStyle={{
        padding: 0,
        backgroundColor: '#f9f3eb',
        borderRadius: '10px',
      }}
    >
      <MainContainer>
        <ContentSection>
          <HeaderSection>
            <IconWrapper color={theme.colors.orange} height={'42.04px'} width={'150px'}>
              <LogoSvg />
            </IconWrapper>
            <TitleContainer>
              <Title>
                <T
                  keyName="High Demand for <CuripodAi>Curipod AI</CuripodAi>"
                  defaultValue="High Demand for <CuripodAi>Curipod AI</CuripodAi>"
                  params={{
                    CuripodAi: <CuripodAi />,
                  }}
                />
              </Title>
              <CuripodAIStarsSvg />
            </TitleContainer>

            <DueToHigh>
              <T
                keyName="To continue using Curipod AI, ask your school administrator about upgrading
              to the school plan"
                defaultValue="To continue using Curipod AI, ask your school administrator about upgrading to the school plan or book a meeting with us to get help."
              />
            </DueToHigh>
          </HeaderSection>
          <InfoBox>
            <IconWrapper height={'100px'} width={'100px'}>
              <CuriConfused />
            </IconWrapper>
            <YoureUnableTo>
              <T
                keyName="Free accounts currently don't have access to AI features due to high
              demand. Schools on a paid plan have unlimited access at all times."
                defaultValue="Free accounts currently don't have access to AI features due to high
              demand. Schools on a paid plan have unlimited access at all times."
              />
            </YoureUnableTo>
          </InfoBox>
        </ContentSection>
        <BottomSection>
          <ButtonContainer>
            <Link
              href="https://calendly.com/lexi-lobdell/curipod-ai-help-15-min"
              passHref
            >
              <a target="_blank" rel="noopener noreferrer">
                <Button
                  type="primary"
                  onClick={() => {
                    Metrics.getLogger().logEvent('Paywall.GetHelp');
                  }}
                  styles={{
                    backgroundColor: theme.colors.orange,
                    borderColor: theme.colors.orange,
                  }}
                >
                  <T keyName="Request a quote" defaultValue="Get help" />
                </Button>
              </a>
            </Link>
            <p style={{ fontSize: 14, marginBottom: 10, marginTop: 30 }}>
              <T
                keyName="Use this <Link>ready-to-share 1-pager</Link> to show your administrator how Curipod is making a difference in your classroom."
                defaultValue="Use this <Link>ready-to-share 1-pager</Link> to show your administrator how Curipod is making a difference in your classroom."
                params={{
                  Link: content => (
                    <Link href="/curipod_teacher_pitch.pdf" passHref>
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          Metrics.getLogger().logEvent('Paywall.Get1Pager');
                        }}
                      >
                        <ChatLink>{content}</ChatLink>
                      </a>
                    </Link>
                  ),
                }}
              />
            </p>
          </ButtonContainer>
        </BottomSection>
      </MainContainer>
    </Modal>
  );
};
